import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { GET_PEOPLE, GET_PEOPLE_CATEGORIES } from './queries';

import { IPerson, IPeopleCategory, IPeopleCategories, IPeople } from '../../../helper/Types';

import { VALoading, VALazyImage } from '../../../components';

// 그룹별 데이터 타입
interface IGroupedPeople {
    category: IPeopleCategory;
    people: IPerson[];
}

function PeopleContainer() {
    const [groupedData, setGroupedData] = useState<IGroupedPeople[]>([]);

    const [infoMaxHeight, setInfoMaxHeight] = useState<number | null>(null);
    const profileRef = useRef<HTMLDivElement>(null);

    const { loading, error, data } = useQuery<IPeople>(GET_PEOPLE, {
        variables: {},
        fetchPolicy: "network-only"
    });

    const categoriesQuery = useQuery<IPeopleCategories>(GET_PEOPLE_CATEGORIES, {
        variables: { includeArchived: true },
        fetchPolicy: "network-only"
    });


    // 데이터를 카테고리별로 그룹핑
    useEffect(() => {
        if (!data?.people || !categoriesQuery.data?.peopleCategories) return;

        const categories = categoriesQuery.data.peopleCategories;
        const people = data.people;

        // 카테고리 순서대로 그룹 생성
        const grouped: IGroupedPeople[] = categories
            .slice()
            .sort((a, b) => a.order - b.order)
            .map(category => {
                const categoryPeople = people
                    .filter(person => person.type === category.uid)
                    .slice()
                    .sort((a, b) => (a.order || 0) - (b.order || 0));

                return {
                    category,
                    people: categoryPeople
                };
            })
            .filter(group => group.people.length > 0);

        // 카테고리 없는 사람들 (기존 데이터 "0"~"7")
        const uncategorized = people.filter(person => {
            return !categories.some(cat => cat.uid === person.type);
        });

        if (uncategorized.length > 0) {
            grouped.push({
                category: { uid: '__uncategorized__', title: '미분류', order: 9999 } as IPeopleCategory,
                people: uncategorized
            });
        }

        setGroupedData(grouped);
    }, [data, categoriesQuery.data]);


    // Principal profile 높이 감지하여 info max-height 설정
    useEffect(() => {
        if (!profileRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setInfoMaxHeight(entry.contentRect.height);
            }
        });

        resizeObserver.observe(profileRef.current);

        return () => resizeObserver.disconnect();
    }, [groupedData]);

    const isLoading = loading || categoriesQuery.loading;
    const hasError = error || categoriesQuery.error;

    return (
        <article className="layout-contents-container">
            <h1 className="hidden">People</h1>

            {isLoading && <VALoading data-size="xlarge" />}
            {hasError && <div>에러가 발생했습니다.</div>}
            {!isLoading && !hasError && groupedData.length === 0 && <div>등록된 데이터가 없습니다.</div>}

            {groupedData.length > 0 && groupedData.map((_item: any, _index: number) => {
                const typeId = _item.category.uid;
                const people = _item.people;
                const isPrincipalGroup = _item.category.order === 0;

                return (
                    <section className="people-group" key={_index} data-principal={_index === 0 }>
                        <h2 className="section-title">{_item.category.title}</h2>

                        {/* Principal */}
                        {isPrincipalGroup && people.length > 0 && people.map((_person: any, _pIndex: number) => {
                            return (
                                <figure className="principal" key={_pIndex}>
                                    <div className="profile">
                                        <div className="photo" ref={profileRef}>
                                            {_person.profile.length > 0 &&
                                                <div className="inner"><img src={_person.profile[0].url} alt="" /></div>
                                            }
                                        </div>
                                    </div>
                                    <figcaption className="info" style={infoMaxHeight ? { maxHeight: infoMaxHeight } : undefined}>
                                        <p className="summary">
                                            <strong className="name">{_person.name}</strong>
                                            {_person.department.split('\n').map((_dept: any, _dIndex: number) => {
                                                return (<Fragment key={_dIndex}>{_dept}<br /></Fragment>)
                                            })}
                                            {_person.email}
                                        </p>
                                        <p className="description">
                                            {_person.description.split('\n').map((_desc: any, _descIndex: number) => {
                                                return (<Fragment key={_descIndex}>{_desc}<br /></Fragment>)
                                            })}
                                        </p>
                                    </figcaption>
                                </figure>
                            );
                        })}

                        {/* 리스트 형식 (Principal이 아닌 경우) */}
                        {!isPrincipalGroup && people.length > 0 && (
                            <ul className="list-people">
                                {people.map((_person: any, _pIndex: number) => {
                                    return (
                                        <li key={_pIndex}>
                                            <figure className="person-item">
                                                <div className="profile">
                                                    {_person.profile.length > 0 &&
                                                        <div className="inner"><VALazyImage src={_person.profile[0].url} alt="" /></div>
                                                    }
                                                </div>
                                                <figcaption className="info">
                                                    <strong className="name">{_person.name}</strong>
                                                    <p>
                                                        {_person.department.split('\n').map((_dept: any, _dIndex: number) => {
                                                            return (<Fragment key={_dIndex}>{_dept}<br /></Fragment>)
                                                        })}
                                                        {_person.email}
                                                    </p>
                                                </figcaption>
                                            </figure>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                )
            })}
        </article>
    );
}

export default PeopleContainer;
